import type { SevenFields } from '../hooks/usePracticeSupabase';

interface Props {
  fields: SevenFields;
  onChange: (fields: SevenFields) => void;
  readOnly?: boolean;
  sequenceNo: 1 | 2 | 3;
}

const feelings = [
  '憤怒', '悲傷', '恐懼', '焦慮', '羞愧',
  '委屈', '孤單', '混亂', '麻木', '平靜',
  '感恩', '喜悅', '期待', '好奇', '還不知道',
];

type EventDetails = {
  complaint: string;
  time: string;
  process: string;
  people: string;
  result: string;
};

const emptyEventDetails: EventDetails = {
  complaint: '',
  time: '',
  process: '',
  people: '',
  result: '',
};

const eventLabels: Array<[keyof EventDetails, string]> = [
  ['complaint', '主訴'],
  ['time', '時間'],
  ['process', '過程'],
  ['people', '人物'],
  ['result', '結果'],
];

function parseEventDescription(value: string): EventDetails {
  const trimmed = value.trim();
  if (!trimmed) return { ...emptyEventDetails };

  const hasStructuredLabel = eventLabels.some(([, label]) =>
    new RegExp(`(?:^|\\n)${label}[：:]`).test(trimmed),
  );

  // Preserve earlier free-form drafts by treating the whole value as the complaint.
  if (!hasStructuredLabel) {
    return { ...emptyEventDetails, complaint: trimmed };
  }

  const details = { ...emptyEventDetails };
  const pattern = /(?:^|\n)(主訴|時間|過程|人物|結果)[：:][ \t]*([\s\S]*?)(?=\n(?:主訴|時間|過程|人物|結果)[：:]|$)/g;
  const keyByLabel: Record<string, keyof EventDetails> = {
    主訴: 'complaint',
    時間: 'time',
    過程: 'process',
    人物: 'people',
    結果: 'result',
  };

  for (const match of trimmed.matchAll(pattern)) {
    const key = keyByLabel[match[1]];
    if (key) details[key] = match[2].trim();
  }

  return details;
}

function composeEventDescription(details: EventDetails): string {
  if (!Object.values(details).some((value) => value.trim())) return '';
  return eventLabels
    .map(([key, label]) => `${label}：${details[key].trim()}`)
    .join('\n');
}

export default function SevenFieldForm({ fields, onChange, readOnly = false, sequenceNo }: Props) {
  const update = (key: keyof SevenFields, value: string | string[]) => {
    if (readOnly) return;
    onChange({ ...fields, [key]: value });
  };

  const toggleFeeling = (f: string) => {
    if (readOnly) return;
    const current = [...fields.emotions];
    const idx = current.indexOf(f);
    if (idx >= 0) {
      update('emotions', current.filter((_, i) => i !== idx));
    } else {
      update('emotions', [...current, f]);
    }
  };

  const eventDetails = parseEventDescription(fields.event_description);
  const eventLength = fields.event_description.trim().length;
  const eventLengthIsValid = eventLength >= 50 && eventLength <= 600;

  const updateEventDetail = (key: keyof EventDetails, value: string) => {
    if (readOnly) return;
    update('event_description', composeEventDescription({ ...eventDetails, [key]: value }));
  };

  const fieldDefs: { key: keyof SevenFields; num: string; label: string; placeholder: string; type: 'textarea' | 'text' | 'emotions' }[] = [
    { key: 'body_sensation', num: '②', label: '身體最先出現什麼感覺？', placeholder: '身體哪裡先有反應？收緊、發熱、沉重、麻木、呼吸改變……', type: 'text' },
    { key: 'emotions', num: '③', label: '你能說出的情緒是什麼？', placeholder: '', type: 'emotions' },
    { key: 'meaning', num: '④', label: '你當時怎麼理解這件事？', placeholder: '保留你的原話——你當下的解釋是什麼？', type: 'text' },
    { key: 'automatic_response', num: '⑤', label: '你慣性做了什麼／沒做什麼？', placeholder: '描述你的自動反應行為，不是人格標籤。', type: 'textarea' },
    { key: 'new_response', num: '⑥', label: '現在願意嘗試哪一個不同回應？', placeholder: '具體、可觀察、有時間點。例如「明天會議前先確認清單」。', type: 'textarea' },
    { key: 'result_gratitude', num: '⑦', label: '結果有何差異？此刻感恩什麼？', placeholder: '可填「尚未完成」；記錄結果和感恩。', type: 'textarea' },
  ];

  return (
    <div className="space-y-6">
      <h3
        className="text-lg text-foreground-800 mb-1"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        Practice {sequenceNo} &middot; 七項短紀錄
      </h3>
      <p className="text-xs text-foreground-400 mb-6">
        這不是考試，也不是檢討。這是讓原本躲在暗處運轉的循環，被帶到可以覺察的位置。
      </p>

      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground-700">
          <span className="text-accent-500 text-xs font-semibold w-5">①</span>
          實際發生了什麼？
        </label>
        <div className="rounded-lg border border-foreground-200 bg-background-100 p-4 space-y-3">
          <p className="text-xs text-foreground-500 leading-relaxed">
            AI Angel 會陪你把事件說清楚。簡短回答以下項目即可；人物可以匿名，時間不確定可填「約某年某月」。
          </p>

          <GuidedInput
            label="主訴"
            value={eventDetails.complaint}
            onChange={(value) => updateEventDetail('complaint', value)}
            placeholder="例如：被騙、無效購買"
            readOnly={readOnly}
          />
          <GuidedInput
            label="時間"
            value={eventDetails.time}
            onChange={(value) => updateEventDetail('time', value)}
            placeholder="yyyy.mm.dd；不確定可填約某年某月"
            readOnly={readOnly}
          />
          <GuidedTextarea
            label="過程"
            value={eventDetails.process}
            onChange={(value) => updateEventDetail('process', value)}
            placeholder="事情怎麼開始？中間發生了什麼？"
            readOnly={readOnly}
          />
          <GuidedInput
            label="人物"
            value={eventDetails.people}
            onChange={(value) => updateEventDetail('people', value)}
            placeholder="有哪些人？可以使用代稱"
            readOnly={readOnly}
          />
          <GuidedTextarea
            label="結果"
            value={eventDetails.result}
            onChange={(value) => updateEventDetail('result', value)}
            placeholder="最後發生了什麼？對你造成什麼影響？"
            readOnly={readOnly}
          />

          {!readOnly && (
            <div
              className={`rounded-md px-3 py-2 text-xs leading-relaxed ${
                eventLengthIsValid
                  ? 'bg-emerald-50 text-emerald-700'
                  : eventLength > 600
                    ? 'bg-rose-50 text-rose-700'
                    : 'bg-amber-50 text-amber-800'
              }`}
              role="status"
              aria-live="polite"
            >
              <div className="flex items-center justify-between gap-3">
                <span>
                  {eventLengthIsValid
                    ? '已經說得夠清楚，可以送出。'
                    : eventLength > 600
                      ? '內容超過 600 字，請稍微精簡。'
                      : '請再補充時間、過程、人物或結果，AI Angel 才能更準確地陪你整理。'}
                </span>
                <span className="shrink-0 font-medium">{eventLength}／50–600 字</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {fieldDefs.map((def) => {
        if (def.key === 'emotions') {
          return (
            <div key={def.key} className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-foreground-700">
                <span className="text-accent-500 text-xs font-semibold w-5">{def.num}</span>
                {def.label}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {feelings.map((f) => {
                  const isActive = fields.emotions.includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      disabled={readOnly}
                      onClick={() => toggleFeeling(f)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-accent-100 border-accent-300 text-accent-800'
                          : 'bg-background-50 border-foreground-200 text-foreground-500 hover:border-foreground-300'
                      } ${readOnly ? 'cursor-default' : ''}`}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
              <input
                type="text"
                value={fields.emotion_free_input}
                onChange={(e) => update('emotion_free_input', e.target.value)}
                readOnly={readOnly}
                placeholder="或自由輸入其他情緒……"
                className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
              />
            </div>
          );
        }

        return (
          <div key={def.key} className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground-700">
              <span className="text-accent-500 text-xs font-semibold w-5">{def.num}</span>
              {def.label}
            </label>
            {def.type === 'textarea' ? (
              <textarea
                value={fields[def.key] as string}
                onChange={(e) => update(def.key, e.target.value)}
                readOnly={readOnly}
                placeholder={def.placeholder}
                rows={3}
                maxLength={def.key === 'event_description' ? 600 : 500}
                className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300 resize-none"
              />
            ) : (
              <input
                type="text"
                value={fields[def.key] as string}
                onChange={(e) => update(def.key, e.target.value)}
                readOnly={readOnly}
                placeholder={def.placeholder}
                className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function GuidedInput({
  label,
  value,
  onChange,
  placeholder,
  readOnly,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  readOnly: boolean;
}) {
  return (
    <div className="grid gap-1.5 md:grid-cols-[4rem_1fr] md:items-center">
      <label className="text-xs font-medium text-foreground-600">{label}：</label>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        maxLength={200}
        className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
      />
    </div>
  );
}

function GuidedTextarea({
  label,
  value,
  onChange,
  placeholder,
  readOnly,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  readOnly: boolean;
}) {
  return (
    <div className="grid gap-1.5 md:grid-cols-[4rem_1fr] md:items-start">
      <label className="text-xs font-medium text-foreground-600 md:pt-2">{label}：</label>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        rows={2}
        maxLength={400}
        className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300 resize-y"
      />
    </div>
  );
}
